/**
 * DataSetForm — render a guidata DataSet schema as a React form.
 *
 * Consumes a JSON Schema 2020-12 document produced by
 * ``guidata.dataset.dataset_to_schema``, augmented with ``x-guidata-*``
 * extension keywords. Each property is dispatched to a leaf widget based
 * on its ``x-guidata-kind`` value.
 *
 * Supported kinds: int, float, bool, string, text, choice,
 * multiple_choice, image_choice, color, date, datetime, file,
 * float_array (graphical grid editor via ArrayEditorDialog),
 * dict (raw JSON textarea).
 *
 * Layout (groups, tabs) is taken from the ``x-guidata-layout`` key.
 * Falls back to a flat property order when that key is missing.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DOMPurify from "dompurify";
import type { DynamicChoice, JsonSchema } from "../../runtime/runtime";
import { t } from "../../i18n/translate";
import { ArrayEditorDialog, normalizeToMatrix } from "../ArrayEditorDialog";
import { HistogramRangeField } from "./HistogramRangeField";
import { histogramRangeState, numericValueValid } from "./rangeValidation";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Values = Record<string, unknown>;

/** Live ``display.active`` overrides keyed by item name. Populated by
 *  ``resolveActive`` round-trips when the form contains items whose active
 *  state is dynamic (``x-guidata-active-dynamic``). An entry of ``false``
 *  greys out the corresponding field; ``true`` re-enables it; a missing
 *  entry falls back to the static ``x-guidata-active`` baked in the schema. */
const ActiveOverridesContext = createContext<Record<string, boolean>>({});

interface FormBehavior {
  autoSliders: boolean;
  onSliderInteraction?: (dragging: boolean) => void;
}

const FormBehaviorContext = createContext<FormBehavior>({
  autoSliders: false,
});

/** Sanitise a guidata label before injecting it as HTML. Labels may
 *  legitimately carry simple inline markup (``<b>``, ``<sub>``, units
 *  like ``m<sup>2</sup>``), so we keep an HTML profile but strip any
 *  script/event-handler payload. */
function sanitizeLabel(html: string): { __html: string } {
  return { __html: DOMPurify.sanitize(html, { USE_PROFILES: { html: true } }) };
}

export interface DataSetFormProps {
  schema: JsonSchema;
  values: Values;
  onChange: (values: Values) => void;
  /** Resolver for dynamic ChoiceItems (when a property carries
   *  ``x-guidata-choices-dynamic``). */
  resolveChoices?: (
    itemName: string,
    currentValues: Values,
  ) => Promise<DynamicChoice[]>;
  /** Resolver for guidata ``display`` callbacks (when a property carries
   *  ``x-guidata-has-callback``).  Runs the callback Python-side and
   *  returns the recomputed values for every item, mirroring the Qt
   *  ``update_widgets`` cascade (e.g. ``ArithmeticParam.operation``). */
  resolveCallbacks?: (
    itemName: string,
    currentValues: Values,
  ) => Promise<Values>;
  /** Resolver for guidata ``display.active`` props (when the form has at
   *  least one item carrying ``x-guidata-active-dynamic``).  Runs the
   *  ``active`` callables Python-side and returns ``{itemName: boolean}``
   *  for every item, mirroring the Qt ``update_widgets`` enable/disable
   *  cascade (e.g. ``BlobOpenCVParam.filter_by_circularity`` gating
   *  ``min_circularity``). */
  resolveActive?: (currentValues: Values) => Promise<Record<string, boolean>>;
  /** Add a range control beside eligible bounded numeric fields. */
  autoSliders?: boolean;
  /** Notify the owner when an automatic slider drag starts or ends. */
  onSliderInteraction?: (dragging: boolean) => void;
  /** Report whether the current values are valid and fully resolved. */
  onStateChange?: (state: { valid: boolean; resolving: boolean }) => void;
}

export function stripTransientValues(
  schema: JsonSchema,
  values: Values,
): Values {
  const properties = (schema.properties as Record<string, JsonSchema>) ?? {};
  return Object.fromEntries(
    Object.entries(values).filter(
      ([name]) => properties[name]?.["x-guidata-transient"] !== true,
    ),
  );
}

export function validateDataSetValues(
  schema: JsonSchema,
  values: Values,
  activeOverrides: Record<string, boolean> = {},
): boolean {
  const properties = (schema.properties as Record<string, JsonSchema>) ?? {};
  const required = new Set((schema.required as string[] | undefined) ?? []);
  for (const [name, prop] of Object.entries(properties)) {
    if (prop["x-guidata-kind"] !== "histogram_range") continue;
    const state = histogramRangeState(
      name,
      prop,
      properties,
      values,
      activeOverrides,
    );
    if (state.editable && !state.valid) return false;
  }
  return Object.entries(properties).every(([name, prop]) => {
    const override = activeOverrides[name];
    const inactive =
      override === false ||
      (override === undefined && prop["x-guidata-active"] === false);
    if (prop.readOnly === true || prop["x-guidata-hide"] === true || inactive) {
      return true;
    }
    const value = values[name];
    if (value == null || value === "") return !required.has(name);
    const kind = prop["x-guidata-kind"];
    if (kind === "int" || kind === "float") {
      if (!numericValueValid(prop, value)) return false;
    }
    if (typeof value === "string" && typeof prop.pattern === "string") {
      try {
        if (!new RegExp(prop.pattern).test(value)) return false;
      } catch {
        return false;
      }
    }
    return true;
  });
}

// Layout node as emitted by the Python side. Either a property name
// (string leaf) or a container with kind/label/items.
type LayoutLeaf = string;
type LayoutContainer = {
  kind: "group" | "tab-group" | "tab" | "separator";
  label?: string;
  items?: LayoutNode[];
};
type LayoutNode = LayoutLeaf | LayoutContainer;

// ---------------------------------------------------------------------------
// Top-level form
// ---------------------------------------------------------------------------

export function DataSetForm(props: DataSetFormProps) {
  const { schema, values, onChange, resolveChoices, resolveCallbacks } = props;
  const {
    resolveActive,
    autoSliders = false,
    onSliderInteraction,
    onStateChange,
  } = props;
  const properties = useMemo(
    () => (schema.properties as Record<string, JsonSchema>) ?? {},
    [schema],
  );
  const layout =
    (schema["x-guidata-layout"] as LayoutNode[] | undefined) ??
    (schema["x-guidata-property-order"] as string[] | undefined) ??
    Object.keys(properties);

  // Whether any item's ``active`` state is dynamic. When true, every edit
  // may flip a sibling's enabled state (e.g. toggling
  // ``filter_by_circularity`` enables ``min_circularity``), so we re-run the
  // ``active`` callables Python-side after each change.
  const hasDynamicActive = useMemo(
    () =>
      Object.values(properties).some(
        (p) => p["x-guidata-active-dynamic"] === true,
      ),
    [properties],
  );

  // Live ``display.active`` overrides keyed by item name (see context doc).
  const [activeOverrides, setActiveOverrides] = useState<
    Record<string, boolean>
  >({});

  // Monotonic counter guarding against out-of-order callback responses:
  // the last edit wins, so a slow Python round-trip can never clobber a
  // newer one (cf. the ``cancelled`` flag in ``useChoices``).
  const resolutionSeq = useRef(0);
  const [resolving, setResolving] = useState(
    hasDynamicActive && resolveActive !== undefined,
  );

  // Resolve the initial active state once (covers dynamic items whose
  // default state depends on sibling defaults). Subsequent edits refresh it
  // through ``setValue`` below.
  useEffect(() => {
    if (!hasDynamicActive || !resolveActive) return;
    const seq = ++resolutionSeq.current;
    setResolving(true);
    void resolveActive(values)
      .then((map) => {
        if (seq !== resolutionSeq.current) return;
        if (map) setActiveOverrides(map);
      })
      .catch((error) =>
        console.error("[dataset] active resolver failed", error),
      )
      .finally(() => {
        if (seq === resolutionSeq.current) setResolving(false);
      });
    // Run only on mount / when the resolver or schema shape changes; per-edit
    // refresh is handled in ``setValue``.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDynamicActive, resolveActive]);

  const setValues = useCallback(
    (name: string, newValues: Values) => {
      const prop = properties[name];
      const seq = ++resolutionSeq.current;
      if (prop?.["x-guidata-kind"] === "histogram_range") {
        const state = histogramRangeState(
          name,
          prop,
          properties,
          newValues,
          activeOverrides,
        );
        if (!state.editable) {
          setResolving(false);
          return;
        }
        onChange(newValues);
        if (!state.valid) {
          setResolving(false);
          return;
        }
      } else {
        onChange(newValues);
      }
      const hasCallback =
        prop?.["x-guidata-has-callback"] === true && resolveCallbacks;
      const needsResolution = Boolean(
        hasCallback || (hasDynamicActive && resolveActive),
      );
      setResolving(needsResolution);
      if (!needsResolution) return;
      void (async () => {
        let resolvedValues = newValues;
        try {
          if (hasCallback && resolveCallbacks) {
            const updated = await resolveCallbacks(name, newValues);
            if (seq !== resolutionSeq.current) return;
            if (updated && Object.keys(updated).length > 0) {
              resolvedValues = { ...newValues, ...updated };
              onChange(resolvedValues);
            }
          }
          if (hasDynamicActive && resolveActive) {
            const map = await resolveActive(resolvedValues);
            if (seq !== resolutionSeq.current) return;
            if (map) setActiveOverrides(map);
          }
        } catch (error) {
          if (seq === resolutionSeq.current) {
            console.error("[dataset] resolver failed", error);
          }
        } finally {
          if (seq === resolutionSeq.current) setResolving(false);
        }
      })();
    },
    [
      onChange,
      properties,
      resolveCallbacks,
      hasDynamicActive,
      resolveActive,
      activeOverrides,
    ],
  );

  const setValue = useCallback(
    (name: string, value: unknown) => {
      setValues(name, { ...values, [name]: value });
    },
    [setValues, values],
  );

  const valid = useMemo(
    () => validateDataSetValues(schema, values, activeOverrides),
    [schema, values, activeOverrides],
  );

  useEffect(() => {
    onStateChange?.({ valid, resolving });
  }, [onStateChange, resolving, valid]);

  return (
    <FormBehaviorContext.Provider value={{ autoSliders, onSliderInteraction }}>
      <ActiveOverridesContext.Provider value={activeOverrides}>
        <div className="dataset-form">
          {layout.map((node, idx) => (
            <LayoutNodeView
              key={idx}
              node={node}
              properties={properties}
              values={values}
              setValue={setValue}
              setValues={setValues}
              resolveChoices={resolveChoices}
            />
          ))}
        </div>
      </ActiveOverridesContext.Provider>
    </FormBehaviorContext.Provider>
  );
}

interface NodeProps {
  node: LayoutNode;
  properties: Record<string, JsonSchema>;
  values: Values;
  setValue: (name: string, value: unknown) => void;
  setValues: (name: string, values: Values) => void;
  resolveChoices?: DataSetFormProps["resolveChoices"];
}

function LayoutNodeView({
  node,
  properties,
  values,
  setValue,
  setValues,
  resolveChoices,
}: NodeProps) {
  if (typeof node === "string") {
    const prop = properties[node];
    if (!prop) return null;
    if (prop["x-guidata-hide"] === true) return null;
    return (
      <FieldRow
        name={node}
        properties={properties}
        prop={prop}
        value={values[node]}
        onChange={(v) => setValue(node, v)}
        onValuesChange={(nextValues) => setValues(node, nextValues)}
        currentValues={values}
        resolveChoices={resolveChoices}
      />
    );
  }
  if (node.kind === "separator") {
    return <hr className="dataset-form-separator" />;
  }
  if (node.kind === "tab-group") {
    return (
      <TabGroup
        node={node}
        properties={properties}
        values={values}
        setValue={setValue}
        setValues={setValues}
        resolveChoices={resolveChoices}
      />
    );
  }
  // group or tab (when used outside a tab-group, "tab" still renders fine
  // as a labelled fieldset)
  return (
    <fieldset className="dataset-form-group">
      {node.label && (
        <legend dangerouslySetInnerHTML={sanitizeLabel(node.label)} />
      )}
      {(node.items ?? []).map((child, idx) => (
        <LayoutNodeView
          key={idx}
          node={child}
          properties={properties}
          values={values}
          setValue={setValue}
          setValues={setValues}
          resolveChoices={resolveChoices}
        />
      ))}
    </fieldset>
  );
}

function TabGroup({
  node,
  properties,
  values,
  setValue,
  setValues,
  resolveChoices,
}: {
  node: LayoutContainer;
  properties: Record<string, JsonSchema>;
  values: Values;
  setValue: (name: string, value: unknown) => void;
  setValues: (name: string, values: Values) => void;
  resolveChoices?: DataSetFormProps["resolveChoices"];
}) {
  const tabs = (node.items ?? []).filter(
    (n): n is LayoutContainer => typeof n !== "string" && n.kind === "tab",
  );
  const [active, setActive] = useState(0);
  if (tabs.length === 0) return null;
  return (
    <div className="dataset-form-tabs">
      <div className="dataset-form-tab-bar">
        {tabs.map((tab, idx) => (
          <button
            key={idx}
            type="button"
            className={idx === active ? "active" : ""}
            onClick={() => setActive(idx)}
            dangerouslySetInnerHTML={sanitizeLabel(
              tab.label || t("Tab {n}", { n: idx + 1 }),
            )}
          />
        ))}
      </div>
      <div className="dataset-form-tab-body">
        {(tabs[active].items ?? []).map((child, idx) => (
          <LayoutNodeView
            key={idx}
            node={child}
            properties={properties}
            values={values}
            setValue={setValue}
            setValues={setValues}
            resolveChoices={resolveChoices}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field row + dispatch
// ---------------------------------------------------------------------------

interface FieldRowProps {
  name: string;
  properties: Record<string, JsonSchema>;
  prop: JsonSchema;
  value: unknown;
  onChange: (v: unknown) => void;
  onValuesChange: (values: Values) => void;
  currentValues: Values;
  resolveChoices?: DataSetFormProps["resolveChoices"];
}

function FieldRow(props: FieldRowProps) {
  const { name, prop } = props;
  // Like guidata on Desktop, a ``BoolItem`` without a label only shows
  // the text of its checkbox.
  const label =
    (prop["x-guidata-label"] as string | undefined) ??
    (prop["x-guidata-kind"] === "bool" && prop["x-guidata-text"] ? "" : name);
  const help = prop.description as string | undefined;
  const unit = prop["x-guidata-unit"] as string | undefined;
  const activeOverrides = useContext(ActiveOverridesContext);
  // An item is non-editable when it is read-only (e.g. ``set_computed``)
  // or when its ``display.active`` resolved to ``False`` (e.g.
  // ``ArithmeticParam.operation``, a preview field updated by callbacks, or
  // ``BlobOpenCVParam.min_circularity`` while its filter group is off). A
  // live override (from ``resolveActive``) wins over the static schema flag.
  const override = activeOverrides[name];
  const readOnly =
    prop.readOnly === true ||
    override === false ||
    (override === undefined && prop["x-guidata-active"] === false);
  if (prop["x-guidata-kind"] === "histogram_range") {
    const state = histogramRangeState(
      name,
      prop,
      props.properties,
      props.currentValues,
      activeOverrides,
    );
    return (
      <div
        className={`dataset-form-row dataset-form-row-histogram${readOnly ? " dataset-form-row-disabled" : ""}`}
        title={help}
      >
        <div className="dataset-form-control">
          <FieldWidget {...props} disabled={!state.editable} />
        </div>
      </div>
    );
  }
  return (
    <div
      className={
        readOnly
          ? "dataset-form-row dataset-form-row-disabled"
          : "dataset-form-row"
      }
    >
      <label
        className="dataset-form-label"
        title={help}
        dangerouslySetInnerHTML={sanitizeLabel(label)}
      />
      <div className="dataset-form-control">
        <FieldWidget {...props} disabled={readOnly} />
        {unit && <span className="dataset-form-unit">{unit}</span>}
      </div>
    </div>
  );
}

function FieldWidget(props: FieldRowProps & { disabled?: boolean }) {
  const kind = props.prop["x-guidata-kind"] as string | undefined;
  const behavior = useContext(FormBehaviorContext);
  switch (kind) {
    case "int":
      return <NumericField {...props} integer />;
    case "float":
      return <NumericField {...props} integer={false} />;
    case "bool":
      return <BoolField {...props} />;
    case "string":
      return <StringField {...props} multiline={false} />;
    case "text":
      return <StringField {...props} multiline />;
    case "choice":
      return <ChoiceField {...props} />;
    case "multiple_choice":
      return <MultipleChoiceField {...props} />;
    case "image_choice":
      return <ImageChoiceField {...props} />;
    case "color":
      return <ColorField {...props} />;
    case "date":
      return <DateField {...props} withTime={false} />;
    case "datetime":
      return <DateField {...props} withTime />;
    case "file":
      return <FileField {...props} />;
    case "float_array":
      return <FloatArrayField {...props} />;
    case "histogram_range": {
      return (
        <HistogramRangeField
          prop={props.prop}
          minimumProp={
            props.properties[
              String(props.prop["x-guidata-minimum-field"] ?? "minimum")
            ]
          }
          maximumProp={
            props.properties[
              String(props.prop["x-guidata-maximum-field"] ?? "maximum")
            ]
          }
          value={props.value}
          values={props.currentValues}
          disabled={props.disabled}
          onValuesChange={props.onValuesChange}
          onSliderInteraction={behavior.onSliderInteraction}
        />
      );
    }
    case "dict":
      return <JsonField {...props} />;
    default:
      return <JsonField {...props} />;
  }
}

// ---------------------------------------------------------------------------
// Leaf widgets
// ---------------------------------------------------------------------------

type LeafProps = FieldRowProps & { disabled?: boolean };

function NumericField({
  name,
  prop,
  value,
  onChange,
  disabled,
  integer,
}: LeafProps & { integer: boolean }) {
  const min = prop.minimum as number | undefined;
  const max = prop.maximum as number | undefined;
  const step = integer
    ? 1
    : ((prop["x-guidata-step"] as number | undefined) ?? "any");
  const slider = prop["x-guidata-slider"] === true;
  const parity = prop["x-guidata-even"] as boolean | undefined;
  const behavior = useContext(FormBehaviorContext);
  const dragActive = useRef(false);
  const finiteBounds =
    typeof min === "number" &&
    Number.isFinite(min) &&
    typeof max === "number" &&
    Number.isFinite(max) &&
    min < max;
  const integerRangeUsable =
    !integer ||
    (Number.isSafeInteger(min) &&
      Number.isSafeInteger(max) &&
      (max as number) - (min as number) < 2 ** 31);
  const nonzeroCrossesZero =
    prop["x-guidata-nonzero"] === true &&
    finiteBounds &&
    (min as number) <= 0 &&
    (max as number) >= 0;
  const matchesParity = (candidate: number) =>
    parity === undefined || Math.abs(candidate % 2) === (parity ? 0 : 1);
  const sliderMin =
    integer && finiteBounds && !matchesParity(min as number)
      ? (min as number) + 1
      : min;
  const sliderMax =
    integer && finiteBounds && !matchesParity(max as number)
      ? (max as number) - 1
      : max;
  const parityRangeUsable =
    !integer ||
    parity === undefined ||
    (sliderMin as number) <= (sliderMax as number);
  const automaticSlider =
    behavior.autoSliders &&
    !slider &&
    prop["x-guidata-auto-slider"] !== false &&
    finiteBounds &&
    integerRangeUsable &&
    parityRangeUsable &&
    !nonzeroCrossesZero;
  const span = finiteBounds ? (max as number) - (min as number) : 0;
  const configuredStep = Number(prop["x-guidata-step"]);
  const sliderStep = integer
    ? parity === undefined
      ? 1
      : 2
    : Number.isFinite(configuredStep) &&
        configuredStep > 0 &&
        span / configuredStep <= 100_000
      ? configuredStep
      : span / 1000;
  const handle = (raw: string) => {
    if (raw === "") {
      onChange(null);
      return;
    }
    const n = integer ? parseInt(raw, 10) : parseFloat(raw);
    onChange(Number.isFinite(n) ? n : null);
  };
  const v = value == null ? "" : String(value);
  const numberInput = (
    <input
      type={slider ? "range" : "number"}
      value={v}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      onChange={(e) => handle(e.target.value)}
    />
  );
  if (!automaticSlider) return numberInput;
  const startDragging = () => {
    if (dragActive.current) return;
    dragActive.current = true;
    behavior.onSliderInteraction?.(true);
  };
  const stopDragging = () => {
    if (!dragActive.current) return;
    dragActive.current = false;
    behavior.onSliderInteraction?.(false);
  };
  return (
    <div className="dataset-form-numeric-slider">
      {numberInput}
      <input
        type="range"
        aria-label={`${name} slider`}
        value={v}
        min={sliderMin}
        max={sliderMax}
        step={sliderStep}
        disabled={disabled}
        onChange={(event) => handle(event.target.value)}
        onPointerDown={startDragging}
        onPointerUp={stopDragging}
        onPointerCancel={stopDragging}
        onBlur={stopDragging}
      />
    </div>
  );
}

function BoolField({ prop, value, onChange, disabled }: LeafProps) {
  const text = prop["x-guidata-text"] as string | undefined;
  return (
    <label className="dataset-form-check">
      <input
        type="checkbox"
        checked={value === true}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      {text && <span>{text}</span>}
    </label>
  );
}

function StringField({
  prop,
  value,
  onChange,
  disabled,
  multiline,
}: LeafProps & { multiline: boolean }) {
  const v = value == null ? "" : String(value);
  const pattern = prop.pattern as string | undefined;
  const password = prop["x-guidata-password"] === true;
  if (multiline) {
    return (
      <textarea
        value={v}
        rows={4}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <input
      type={password ? "password" : "text"}
      value={v}
      pattern={pattern}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

interface ChoiceEntry {
  value: unknown;
  label: string;
  icon?: string;
}

function useChoices(
  prop: JsonSchema,
  name: string,
  currentValues: Values,
  resolveChoices?: DataSetFormProps["resolveChoices"],
): ChoiceEntry[] {
  const isDynamic = prop["x-guidata-choices-dynamic"] === true;
  const staticChoices =
    (prop["x-guidata-choices"] as ChoiceEntry[] | undefined) ?? [];
  const [dynamic, setDynamic] = useState<ChoiceEntry[]>([]);
  // Re-resolve on dependencies changing values (cheap: front-end serialises
  // by JSON of the visible state).
  const valuesKey = useMemo(
    () => JSON.stringify(currentValues),
    [currentValues],
  );
  // NOTE: this MUST be ``useEffect`` (not ``useMemo``).  ``useMemo`` does
  // not run cleanup callbacks, so the ``cancelled`` flag would never flip
  // and the *last-resolving* request would win — a real race condition
  // when ``currentValues`` change faster than the Python round-trip.
  useEffect(() => {
    if (!isDynamic || !resolveChoices) return;
    let cancelled = false;
    resolveChoices(name, currentValues).then((entries) => {
      if (!cancelled) setDynamic(entries);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDynamic, name, valuesKey, resolveChoices]);
  return isDynamic ? dynamic : staticChoices;
}

function ChoiceField(props: LeafProps) {
  const {
    prop,
    value,
    onChange,
    disabled,
    currentValues,
    name,
    resolveChoices,
  } = props;
  const choices = useChoices(prop, name, currentValues, resolveChoices);
  return (
    <select
      value={String(value ?? "")}
      disabled={disabled}
      onChange={(e) => {
        const idx = e.target.selectedIndex;
        onChange(choices[idx]?.value);
      }}
    >
      {choices.map((c, idx) => (
        <option key={idx} value={String(c.value)}>
          {c.label}
        </option>
      ))}
    </select>
  );
}

function MultipleChoiceField(props: LeafProps) {
  const {
    prop,
    value,
    onChange,
    disabled,
    currentValues,
    name,
    resolveChoices,
  } = props;
  const choices = useChoices(prop, name, currentValues, resolveChoices);
  const selected = new Set(Array.isArray(value) ? value : []);
  return (
    <div className="dataset-form-multichoice">
      {choices.map((c, idx) => (
        <label key={idx} className="dataset-form-check">
          <input
            type="checkbox"
            checked={selected.has(c.value)}
            disabled={disabled}
            onChange={(e) => {
              const next = new Set(selected);
              if (e.target.checked) next.add(c.value);
              else next.delete(c.value);
              onChange(Array.from(next));
            }}
          />
          <span>{c.label}</span>
        </label>
      ))}
    </div>
  );
}

function ImageChoiceField(props: LeafProps) {
  const {
    prop,
    value,
    onChange,
    disabled,
    currentValues,
    name,
    resolveChoices,
  } = props;
  const choices = useChoices(prop, name, currentValues, resolveChoices);
  return (
    <div className="dataset-form-imagechoice">
      {choices.map((c, idx) => {
        const sel = c.value === value;
        return (
          <button
            type="button"
            key={idx}
            className={sel ? "selected" : ""}
            disabled={disabled}
            onClick={() => onChange(c.value)}
            title={c.label}
          >
            {c.icon && <img src={c.icon} alt="" />}
            <span>{c.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function ColorField({ value, onChange, disabled }: LeafProps) {
  const v = typeof value === "string" && value ? value : "#000000";
  return (
    <input
      type="color"
      value={v}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function DateField({
  value,
  onChange,
  disabled,
  withTime,
}: LeafProps & { withTime: boolean }) {
  // Browsers expect "YYYY-MM-DD" or "YYYY-MM-DDTHH:MM" — we strip seconds/ms
  // from the ISO form Python produces.
  const raw = typeof value === "string" ? value : "";
  const v = withTime ? raw.slice(0, 16) : raw.slice(0, 10);
  return (
    <input
      type={withTime ? "datetime-local" : "date"}
      value={v}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value || null)}
    />
  );
}

function FileField({ prop, value, onChange, disabled }: LeafProps) {
  // The browser cannot select arbitrary local paths. We expose a free-text
  // input as a placeholder; a richer picker requires File System Access API.
  const mode = prop["x-guidata-file-mode"] as string | undefined;
  const isMulti = mode === "open-multi";
  if (isMulti) {
    const list = Array.isArray(value) ? (value as unknown[]).join("; ") : "";
    return (
      <input
        type="text"
        value={list}
        placeholder={t("path1; path2; …")}
        disabled={disabled}
        onChange={(e) =>
          onChange(
            e.target.value
              .split(";")
              .map((s) => s.trim())
              .filter(Boolean),
          )
        }
      />
    );
  }
  const v = typeof value === "string" ? value : "";
  return (
    <input
      type="text"
      value={v}
      placeholder={mode === "directory" ? t("directory path") : t("file path")}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function FloatArrayField({ prop, value, onChange, disabled }: LeafProps) {
  const [editing, setEditing] = useState(false);
  const format = prop["x-guidata-format"] as string | undefined;
  const transpose = prop["x-guidata-transpose"] === true;
  const variableSize = prop["x-guidata-variable-size"] === true;
  const { matrix, is1D } = normalizeToMatrix(value);
  const rows = matrix.length;
  const cols = rows > 0 ? matrix[0].length : 0;
  const shape = is1D ? `${rows}` : `${rows}×${cols}`;
  return (
    <div className="dataset-form-array">
      <span className="dataset-form-array-summary">
        {rows === 0 ? t("empty array") : t("array [{shape}]", { shape })}
      </span>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setEditing(true)}
      >
        {disabled ? t("View…") : t("Edit…")}
      </button>
      {editing && (
        <ArrayEditorDialog
          value={value}
          format={format}
          transpose={transpose}
          variableSize={variableSize && !disabled}
          readOnly={disabled}
          onCancel={() => setEditing(false)}
          onSubmit={(next) => {
            onChange(next);
            setEditing(false);
          }}
        />
      )}
    </div>
  );
}

function JsonField({ value, onChange, disabled }: LeafProps) {
  const [text, setText] = useState(() =>
    JSON.stringify(value ?? null, null, 2),
  );
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="dataset-form-json">
      <textarea
        rows={4}
        value={text}
        disabled={disabled}
        onChange={(e) => {
          setText(e.target.value);
          try {
            onChange(JSON.parse(e.target.value));
            setError(null);
          } catch (err) {
            setError((err as Error).message);
          }
        }}
      />
      {error && <span className="dataset-form-error">{error}</span>}
    </div>
  );
}
